/**
 * Data layer for `advertiser-signup`. Thin typed fetch wrappers over /api/v1/advertiser-signup.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type AdvertiserSignupStatus = "pending" | "approved" | "rejected";

export interface AdvertiserSignupRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	company_name?: string;
	contact_name?: string;
	email?: string;
	phone?: string;
	country?: string;
	website?: string;
	vertical?: string;
	monthly_budget_usd?: number;
	notes?: string;
	status?: AdvertiserSignupStatus;
	review_reason?: string;
	reviewed_at?: number;
	[key: string]: unknown;
}

export interface ListAdvertiserSignupResponse {
	count: number;
	items: AdvertiserSignupRow[];
}

export interface CreateAdvertiserSignupBody {
	[key: string]: unknown;
}

export interface ReviewAdvertiserSignupBody {
	status: "approved" | "rejected";
	reason?: string;
}

const BASE = "/api/v1/advertiser-signup";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	status?: AdvertiserSignupStatus | "all",
): Promise<ListAdvertiserSignupResponse> {
	const url =
		status && status !== "all"
			? `${BASE}?status=${encodeURIComponent(status)}`
			: BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAdvertiserSignupResponse>(res);
}

export async function create(
	body: CreateAdvertiserSignupBody,
): Promise<AdvertiserSignupRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<AdvertiserSignupRow>(res);
}

export async function review(
	id: string,
	body: ReviewAdvertiserSignupBody,
): Promise<AdvertiserSignupRow> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}/review`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<AdvertiserSignupRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const advertiserSignupQueryKeys = {
	all: ["advertiser-signup"] as const,
	list: (status?: AdvertiserSignupStatus | "all") =>
		[...advertiserSignupQueryKeys.all, status ?? "all"] as const,
	detail: (id: string) => [...advertiserSignupQueryKeys.all, id] as const,
};
